import React, { useMemo } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { Tooltip } from "antd5";

import { truncate } from "lib/utils";
import { assertCurrentUser } from "../../../../lib/currentUser";
import {
  AssignProcurementStage,
  CommentProcurementStage,
  MentionProcurementStage,
  NotificationDto,
  NotificationDtoTextAttributes,
  QualifyProcurementStage,
} from "../../../../lib/generated/app-api";
import { useMarkNotificationsAsRead } from "../../../../lib/hooks/api/notifications/useMarkNotificationsAsRead";
import { useMarkNotificationsAsUnread } from "../../../../lib/hooks/api/notifications/useMarkNotificationsAsUnread";
import { EventNames, useTracking } from "../../../../lib/tracking";
import { toHumanRelativeTimeFromNow } from "../../../../lib/utils/relative_time";
import { Flex } from "../../../../styles/utility-components";
import TextButton from "../../../actions/TextButton";
import UserInitials from "../../../comments/UserInitials";

function assertTextAttributes(
  verb: "comment",
  textAttrs: NotificationDtoTextAttributes,
): CommentProcurementStage;
function assertTextAttributes(
  verb: "qualify",
  textAttrs: NotificationDtoTextAttributes,
): QualifyProcurementStage;
function assertTextAttributes(
  verb: "assign",
  textAttrs: NotificationDtoTextAttributes,
): AssignProcurementStage;
function assertTextAttributes(
  verb: "mention",
  textAttrs: NotificationDtoTextAttributes,
): MentionProcurementStage;
function assertTextAttributes(
  verb: string,
  textAttrs: NotificationDtoTextAttributes,
):
  | QualifyProcurementStage
  | AssignProcurementStage
  | CommentProcurementStage
  | MentionProcurementStage
  | never {
  switch (verb) {
    case "comment": {
      const attrs = textAttrs.commentProcurementStage;

      if (!attrs) throw `Invalid notification ${JSON.stringify(textAttrs)}`;

      return attrs;
    }
    case "assign": {
      const attrs = textAttrs.assignProcurementStage;

      if (!attrs) throw `Invalid notification ${JSON.stringify(textAttrs)}`;

      return attrs;
    }
    case "qualify": {
      const attrs = textAttrs.qualifyProcurementStage;

      if (!attrs) throw `Invalid notification ${JSON.stringify(textAttrs)}`;

      return attrs;
    }
    case "mention": {
      const attrs = textAttrs.mentionProcurementStage;

      if (!attrs) throw `Invalid notification ${JSON.stringify(textAttrs)}`;

      return attrs;
    }
    default:
      throw `invalid verb ${verb}`;
  }
}

const DELETED_COMMENT_PLACEHOLDER = "[This comment was deleted]";

export function Notification({ notification }: { notification: NotificationDto }) {
  const { mutate: markAsRead } = useMarkNotificationsAsRead();
  const { mutate: markAsUnread } = useMarkNotificationsAsUnread();
  const { logEvent } = useTracking();

  const notificationText = useMemo(() => {
    switch (notification.verb) {
      case "comment": {
        const { actorName, recordTitle, commentText, deletedAt } = assertTextAttributes(
          notification.verb,
          notification.textAttributes,
        );

        const truncatedRecordTitle = truncate(recordTitle, 50);
        const commentFragment = deletedAt ? (
          <em>{DELETED_COMMENT_PLACEHOLDER}</em>
        ) : (
          <q>{truncate(commentText, 50)}</q>
        );

        return (
          <>
            <b>{actorName}</b> commented on &quot;<b>{truncatedRecordTitle}</b>&quot;:
            <br />
            {commentFragment}
          </>
        );
      }
      case "qualify": {
        const { actorName, recordTitle, toStatusLabel } = assertTextAttributes(
          notification.verb,
          notification.textAttributes,
        );
        const truncatedRecordTitle = truncate(recordTitle, 50);
        return (
          <>
            <b>{actorName}</b> changed the qualification status of &quot;
            <b>{truncatedRecordTitle}</b>&quot; to {toStatusLabel}
          </>
        );
      }
      case "assign": {
        const { actorName, recordTitle, oldAssignee, newAssignee } = assertTextAttributes(
          notification.verb,
          notification.textAttributes,
        );
        const truncatedRecordTitle = truncate(recordTitle, 50);
        const currentUser = assertCurrentUser();

        let oldAssigneeText: string;
        if (oldAssignee) {
          if (oldAssignee.guid) {
            oldAssigneeText = oldAssignee.guid === currentUser.guid ? "you" : oldAssignee.name;
          } else {
            oldAssigneeText = "[User no longer exists]";
          }
        } else {
          oldAssigneeText = "Unassigned";
        }

        let newAssigneeText: string;
        if (newAssignee) {
          if (newAssignee.guid) {
            newAssigneeText = newAssignee.guid === currentUser.guid ? "you" : newAssignee.name;
          } else {
            newAssigneeText = "[User no longer exists]";
          }
        } else {
          newAssigneeText = "Unassigned";
        }

        return (
          <>
            <b>{actorName}</b> has assigned &quot;<b>{truncatedRecordTitle}</b>&quot; from{" "}
            {oldAssigneeText} to {newAssigneeText}
          </>
        );
      }
      case "mention": {
        const { actorName, recordTitle, commentText, deletedAt } = assertTextAttributes(
          notification.verb,
          notification.textAttributes,
        );

        const truncatedRecordTitle = truncate(recordTitle, 50);
        const commentFragment = deletedAt ? (
          <em>{DELETED_COMMENT_PLACEHOLDER}</em>
        ) : (
          <q>{truncate(commentText, 50)}</q>
        );

        return (
          <>
            <b>{actorName}</b> mentioned you in &quot;<b>{truncatedRecordTitle}</b>&quot;:
            <br />
            {commentFragment}
          </>
        );
      }
      default:
        throw `Unsupported notification type ${notification.verb}`;
    }
  }, [notification.verb, notification.textAttributes]);

  const toggleReadStatusBtn = useMemo(() => {
    let label: string;
    let cb: () => void;

    if (notification.readAt) {
      label = "Mark as unread";
      cb = () => markAsUnread([notification.id]);
    } else {
      label = "Mark as read";
      cb = () => markAsRead({ notificationIds: [notification.id], contextSource: "button" });
    }

    return (
      <TextButton
        onClick={(e) => {
          // So we don't navigate to the link
          e.preventDefault();
          // So we don't close the dropdown
          e.stopPropagation();
          cb();
        }}
        className={`${markAsReadStyle}`}
      >
        {label}
      </TextButton>
    );
  }, [notification.readAt, notification.id, markAsRead, markAsUnread]);

  return (
    <NotificationContainer>
      <NotificationHeader>
        <div>
          <span>
            <Tooltip title={notification.createdAt.substr(0, 10)}>
              {toHumanRelativeTimeFromNow(notification.createdAt)}
            </Tooltip>{" "}
            {!notification.readAt && <RedDot />}
          </span>
        </div>
        {toggleReadStatusBtn}
      </NotificationHeader>
      <NotificationLink
        href={notification.link}
        onClick={(e) => {
          // stop the link navgiating away before marking as read
          e.preventDefault();
          logEvent(EventNames.notificationClick, {
            "Destination url": notification.link,
            "Notification ID": notification.id,
          });
          markAsRead({ notificationIds: [notification.id], contextSource: "notification" });
          window.location.href = notification.link;
        }}
      >
        <UserInitialsContainer>
          <UserInitials
            firstName={notification.actor.firstName}
            lastName={notification.actor.lastName}
          />
        </UserInitialsContainer>
        <PreWrap>{notificationText}</PreWrap>
      </NotificationLink>
    </NotificationContainer>
  );
}

const NotificationContainer = styled.div({
  display: "flex",
  flexDirection: "column",
  "&:hover": {
    display: "block",
  },
});

const NotificationHeader = styled(Flex)({
  justifyContent: "space-between",
  width: "100%",
  paddingLeft: 50,
});

const RedDot = styled.span(({ theme }) => ({
  height: 5,
  width: 5,
  borderRadius: "50%",
  backgroundColor: theme.colors.sysDestructiveDefault,
  display: "inline-block",
  marginBottom: "2.5px",
  marginLeft: 8,
}));

const NotificationLink = styled.a({
  display: "flex",
  alignItems: "center",
  width: "100%",
  gap: 18,
  color: "inherit",
});

const UserInitialsContainer = styled.div({
  display: "flex",
  alignItems: "center",
  marginBottom: 21,
});

const PreWrap = styled.div({
  whiteSpace: "pre-wrap",
});

const markAsReadStyle = css({
  display: "none",
  fontSize: 12,
});
